import React, { FC, useEffect, useRef, useState } from 'react';
import { Button, Drawer, Form, Input } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { NewPasswordFormField } from 'common/components/NewPasswordFormField';
import { rules } from 'common/helpers/form.helper';
import { useCaptcha } from 'common/hooks/useCaptcha';
import { IChangeUserPasswordByCodePayload, ISendChangeUserPasswordCodePayload } from 'entities/User/User.models';

const COOLDOWN_SECONDS = 60;

type ChangePasswordChannel = 'email' | 'phone';

interface IComponentProps {
  open: boolean;
  error: string | null;
  onClose: () => void;
  onSendCode: (channel: ChangePasswordChannel, payload: ISendChangeUserPasswordCodePayload) => Promise<void>;
  onConfirm: (channel: ChangePasswordChannel, value: IChangeUserPasswordByCodePayload) => void;
}

export const ChangePasswordCard: FC<IComponentProps> = ({ open, error, onClose, onSendCode, onConfirm }) => {
  const [form] = useForm();
  const [codeSent, setCodeSent] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [channel, setChannel] = useState<ChangePasswordChannel | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { trigger, loading, captchaNode } = useCaptcha();

  useEffect(() => {
    if (error) {
      form.setFields([{ name: 'code', errors: [error] }]);
    }
  }, [error]);

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setCodeSent(false);
      setCooldown(0);
      setChannel(null);

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  }, [open]);

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const codeSentMessage = channel === 'phone' ? 'Код отправлен на телефон' : 'Код отправлен на email';

  const startCooldown = () => {
    setCooldown(COOLDOWN_SECONDS);

    intervalRef.current = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }

          return 0;
        }

        return prev - 1;
      });
    }, 1000);
  };

  const handleSubmit = (selectedChannel: ChangePasswordChannel): void => {
    setChannel(selectedChannel);
    trigger(() =>
      onSendCode(selectedChannel, {
        onSuccess: () => {
          setCodeSent(true);
          startCooldown();
        },
      }),
    );
  };

  return (
    <Drawer rootClassName="redesign drawer" open={open} onClose={loading ? undefined : onClose} width={720} destroyOnClose>
      <div className="drawer__title">Изменение пароля</div>

      <div className="drawer__body">
        <Form
          layout="vertical"
          form={form}
          requiredMark={false}
          onFinish={({ code, newPassword }) =>
            onConfirm(channel as ChangePasswordChannel, {
              code,
              newPassword,
              onSuccess: () => {
                form.resetFields();
                setCodeSent(false);
                setCooldown(0);
                setChannel(null);

                if (intervalRef.current) {
                  clearInterval(intervalRef.current);
                  intervalRef.current = null;
                }
              },
            })
          }
        >
          <Form.Item className="mb-9" label="Код подтверждения">
            <div className="flex align-center gap-4 mb-4">
              <Button
                className="button-sm secondary"
                onClick={() => handleSubmit('email')}
                loading={loading && !codeSent && channel === 'email'}
                disabled={cooldown > 0 || (loading && !codeSent)}
              >
                {cooldown > 0 && channel === 'email' ? `Получить код на email (${cooldown})` : 'Получить код на email'}
              </Button>

              <Button
                className="button-sm secondary"
                onClick={() => handleSubmit('phone')}
                loading={loading && !codeSent && channel === 'phone'}
                disabled={cooldown > 0 || (loading && !codeSent)}
              >
                {cooldown > 0 && channel === 'phone' ? `Получить код на телефон (${cooldown})` : 'Получить код на телефон'}
              </Button>

              {codeSent && cooldown > 0 && <span className="ff-regular fs-xs color-dark-grey">{codeSentMessage}</span>}
            </div>

            <Form.Item className="mb-0" name="code" rules={[rules.required()]}>
              <Input placeholder="Введите код" />
            </Form.Item>
          </Form.Item>

          <NewPasswordFormField label="Новый пароль" name="newPassword" />
        </Form>
      </div>

      {captchaNode}

      <div className="drawer__footer">
        <Button className="button-lg primary" onClick={() => form.submit()} loading={loading} disabled={!channel}>
          Изменить пароль
        </Button>

        <Button className="button-lg secondary" onClick={onClose} disabled={loading}>
          Отмена
        </Button>
      </div>
    </Drawer>
  );
};
